import {Trans} from "@lingui/react/macro";
import {useNavigate} from "react-router";
import * as v from "valibot";
import createId, {IdType} from "../../models/create-id";
import type {CategoryGroupEntryId, GroupRepo} from "../../models/GroupRepo";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {TextField} from "../form/fields";
import {useForm} from "../form/Form";
import {setCatGroup, useCatGroup} from "./useCategoryGroup";

const categoryFormSchema = v.object({
  catGroupName: v.pipe(v.string(), v.nonEmpty(), v.maxLength(256)),
});

export const CreateCatGroupDialog = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const navigate = useNavigate();
  const group = groupRepo.vals.useValues();
  const currCatGroup = useCatGroup(group.id, groupRepo);
  const {Form, field} = useForm({
    initial: {catGroupName: ""},
    schema: categoryFormSchema,
    onSubmit: ({catGroupName}) => {
      const catGroupId = createId(IdType.CategoryGroup);
      groupRepo.store.transaction(() => {
        groupRepo.CategoryGroups.set(catGroupId, {id: catGroupId, name: catGroupName});
        const ids = groupRepo.indexes.getSliceRowIds(
          "sortedCategoryEntries",
          currCatGroup.id
        ) as CategoryGroupEntryId[];
        const catEntries = groupRepo.CategoryGroupEntries.getAll();
        for (const id of ids) {
          const catEntry = catEntries[id];
          const nextCatEntryId = createId(IdType.CategoryGroupEntry);
          groupRepo.CategoryGroupEntries.set(nextCatEntryId, {
            ...catEntry,
            id: nextCatEntryId,
            categoryGroupId: catGroupId,
          });
        }
      });
      setCatGroup(group.id, catGroupId);
      navigate("..");
    },
  });

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <Text as="h1" size="20">
        Add Category Group
      </Text>
      <Form className={css({display: "flex", flexDir: "column", sp: "16"})}>
        <TextField
          field={field("catGroupName")}
          label={<Trans>Name</Trans>}
          data-drawer-autofocus
        />
      </Form>
    </Col>
  );
};

export const ManageCatGroupDialog = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const currCatGroup = useCatGroup(group.id, groupRepo);
  const allCatGroups = Object.values(groupRepo.CategoryGroups.useGetAll());
  const mayDelete = allCatGroups.length > 1;
  const {Form, field} = useForm({
    initial: {catGroupName: currCatGroup.name || "Default"},
    schema: categoryFormSchema,
    onSubmit: ({catGroupName}) => {
      groupRepo.CategoryGroups.patch(currCatGroup.id, {name: catGroupName});
    },
  });

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <Row wrap sp="12">
        <Text as="h1" size="20">
          Manage Category Group
        </Text>
        <Row sp="4" ml="auto">
          <Button to="../create-category-group">
            <Icon name="plus" />
          </Button>
          {mayDelete && (
            <Button to="../delete-category-group">
              <Icon name="trash" />
            </Button>
          )}
        </Row>
      </Row>
      <Form className={css({display: "flex", flexDir: "column", sp: "16"})}>
        <TextField
          field={field("catGroupName")}
          label={<Trans>Name</Trans>}
          data-drawer-autofocus
        />
      </Form>
    </Col>
  );
};

export const DeleteCatGroupDialog = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const currCatGroup = useCatGroup(group.id, groupRepo);
  const allCatGroups = Object.values(groupRepo.CategoryGroups.useGetAll());
  const mayDelete = allCatGroups.length > 1;
  const navigate = useNavigate();

  const handleDeleteCatGroup = () => {
    groupRepo.store.transaction(() => {
      groupRepo.CategoryGroups.delete(currCatGroup.id);
      const ids = groupRepo.indexes.getSliceRowIds(
        "sortedCategoryEntries",
        currCatGroup.id
      ) as CategoryGroupEntryId[];
      for (const id of ids) {
        groupRepo.CategoryGroupEntries.delete(id);
      }
    });
    const nextCatGroup = allCatGroups.find(({id}) => id !== currCatGroup.id);
    if (nextCatGroup) {
      setCatGroup(group.id, nextCatGroup.id);
    }
    navigate("..");
  };

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      {mayDelete ? (
        <>
          <Row wrap sp="12">
            <Text as="h1" size="20">
              Delete {currCatGroup.name} Group
            </Text>
          </Row>
          <Text size="14_tight">Are you sure?</Text>
          <Row sp="8">
            <Button variant="primary" onClick={handleDeleteCatGroup}>
              Yes, Delete
            </Button>
            <Button to="..">Cancel</Button>
          </Row>
        </>
      ) : (
        <Text size="14_tight">Can't delete the last category group.</Text>
      )}
    </Col>
  );
};
